import { useContext, useCallback } from 'react';
import { CommandCenterStateProviderContext } from '../context';
import { AllOnBoardSchedule, ContactStation, ScheduleData, ScheduleTask } from '../types';
import CommandCenterService from '../../../service/CommandCenterService';
import useTrueTwin from './useTrueTwin';
import useUrlParams from './useUrlParams';
import useLoader from './useLoader';
import { showToastMessage } from '../../../utils/commonFunctions';
import useSatellite from './useSatellite';
import GroundStationService from '../../../service/GroundStationService';
import useScenario from './useScenario';
import { isOperateMode } from './utils';

const useContacts = () => {
    const context = useContext(CommandCenterStateProviderContext);
    const { setLoading } = useLoader()
    const { truetwin_id } = useTrueTwin()
    const { selected_satellite } = useSatellite()
    const { scenario_id, sat_id, dashboard, mission_id, sat_grp_id } = useUrlParams()
    const { scenario } = useScenario()
    const operateMode = isOperateMode(dashboard, scenario)

    if (context === undefined) {
        throw new Error('useContacts must be used within a ContactProvider');
    }

    const {
        all_ground_stations,
        set_all_ground_stations,
        selectedScheduleTask,
        setSelectedScheduleTask,
        selectedContact,
        setSelectedContact,
        contacts,
        configured_gs,
        supportedSystemFaults,
        injectedFaultList,
        setContacts,
        timelineContactData,
        planingContactData,
        scheduleData,
        timelineScheduleData,
        scheduleDetails,
        allOnBoardSchedule,
        selectedSchedule,
        setScheduleDetails,
        setScheduleData,
        setTimelineScheduleData,
        setSelectedSchedule,
        setAllOnBoardSchedule,
        setPlaningContactData,
        setTimelineContactData,
        set_configured_gs,
        setSupportedSystemFaults,
        setInjectedFaultList,

        setOnboardScheduleDetails
    } = context;


    const resetScheduleData = () => {
        setAllOnBoardSchedule({} as AllOnBoardSchedule)
        setScheduleDetails({
            start_time: 0,
            name: '',
        })
        setScheduleData([])
        setTimelineScheduleData([])
        setSelectedSchedule(null)
        setLoading('schedule', false)
    }

    const resetContactData = () => {
        setPlaningContactData([])
        setTimelineContactData([])
        set_configured_gs([])
    }

    const get_timeline_onboard_schedule_data = () => {
        CommandCenterService.getOnBoardScheduleData(selected_satellite?.value?.['simulationId']).then((res) => {
            if (res?.data) {
                const Data = res.data
                setScheduleData(Data)
                setTimelineScheduleData(Data)
            }
        }).catch(err => {
            console.log(err)
        })
    }

    const get_ongoing_schedule = () => {
        if (operateMode) {
            CommandCenterService.getOngoingSchedule(scenario_id, sat_id).then((res) => {
                if (res?.data?.data) {
                    select_schedule(res?.data?.data?.[0])
                }
            }).catch(err => {
                console.log(err)
            })
        }
    }


    const get_onboard_schedule_data = () => {
        if (!operateMode) {
            setLoading('schedule', true)
            CommandCenterService.getSimulationStatus(selected_satellite?.value?.simulationId).then((res) => {
                if (res?.data?.['taskList']) {
                    const Data = res?.data?.['taskList']
                    setScheduleDetails({
                        start_time: res?.data?.['simulation_start_epoch'],
                        name: res?.data?.['scheduleName'],
                    })
                    const schedule_data = structure_schedule_date(Data)
                    setScheduleData(schedule_data)
                    setTimelineScheduleData(schedule_data)
                } else {
                    resetScheduleData()
                    setLoading('schedule', false)
                }
            }).catch(err => {
                resetScheduleData()
                showToastMessage('error', 'Failed to get schedule details');
                setLoading('schedule', false)
            })
        }
    }

    const get_configured_gs = () => {
        if (!mission_id || !sat_id || !operateMode) return
        CommandCenterService.getConfiguredGS(mission_id, sat_id).then((res) => {
            if (res?.data && res?.data?.['groundStations']?.length > 0) {
                set_configured_gs(res?.data?.['groundStations'])
            }
        }).catch(err => {
            console.log(err)
        })
    }

    const get_unique_gs = (all_stations: ContactStation[]): any[] => {
        const stations = new Map<string, any>();
        for (const station of all_stations) {
            if (!stations.has(station.groundStationId)) {
                stations.set(station.groundStationId, {
                    groundStationId: station?.groundStationId,
                    groundStationName: station?.groundStationName,
                    groundStationTime: station?.startTime,
                    properties: station?.groundStationProperties
                });
            }
        }
        return Array.from(stations.values());
    }

    const get_gs_contact_details = () => {
        if (!operateMode) {

            setLoading('contacts', true)
            CommandCenterService.getGroundContacts(mission_id, scenario_id, sat_id).then((res) => {
                if (res?.['data']?.['data']) {
                    const ground_stations = res?.['data']?.['data']
                    const unique_stations = get_unique_gs(ground_stations)
                    set_configured_gs(unique_stations)
                    setTimelineContactData(ground_stations)
                    const all_stations: any = {
                        stations: {},
                        contacts: {}
                    }
                    ground_stations.forEach(contact => {
                        if (all_stations.contacts[contact?.['groundStationId']]) {
                            all_stations.contacts[contact?.['groundStationId']].push(contact)
                        } else {
                            all_stations.stations[contact?.['groundStationId']] = contact?.['groundStationName']
                            all_stations.contacts[contact?.['groundStationId']] = [contact]
                        }
                    })
                    setPlaningContactData(all_stations)
                } else {
                    resetContactData()
                    setLoading('contacts', false)
                }
            }).catch(() => {
                resetContactData()
                showToastMessage('error', 'Failed to get contact details');
                setLoading('contacts', false)
            })
        }
    }

    const get_time_based_gs_contact_details = (scenarioId: string, satelliteId: string, startTime: number, endTime: number, mode: string) => {
        setLoading('contacts', true)
        CommandCenterService.getTimeBasedGroundContacts(scenarioId, satelliteId, startTime, endTime, mode).then((res) => {
            if (res?.['data']?.['data']) {
                const ground_stations = res?.['data']?.['data']
                set_configured_gs(ground_stations)
                setTimelineContactData(ground_stations)
                const all_stations: any = {
                    stations: {},
                    contacts: {}
                }
                ground_stations?.forEach(contact => {
                    if (all_stations.contacts[contact?.['groundStationId']]) {
                        all_stations.contacts[contact?.['groundStationId']].push(contact)
                    } else {
                        all_stations.stations[contact?.['groundStationId']] = contact?.['groundStationName']
                        all_stations.contacts[contact?.['groundStationId']] = [contact]
                    }
                })
                setPlaningContactData(all_stations)
            } else {
                resetContactData()
                setLoading('contacts', false)
            }
        }).catch((error) => {
            resetContactData()
            showToastMessage('error', 'Failed to get contact details');
            setLoading('contacts', false)
        })
    }

    const post_contact = (payload: any) => {
        CommandCenterService.postContact(scenario_id, sat_id, payload).then((res) => {
            showToastMessage('success', res?.data?.message);
        }).catch((error) => {
            showToastMessage('error', 'Failed to create contact');
        })
    }

    const book_contact = (contactId: string) => {
        CommandCenterService.book_contact(contactId, scenario_id).then((res) => {
            setSelectedContact((prev) => ({ ...prev!, contact_data: { ...prev!.contact_data, status: 'Booked' } }))
            showToastMessage('success', res?.data?.message)
        }).catch((error) => {
            showToastMessage('error', 'Failed to book contact');
        })
    }

    const unbook_contact = (contactId: string) => {
        CommandCenterService.unbook_contact(contactId, scenario_id).then((res) => {
            setSelectedContact((prev) => ({ ...prev!, contact_data: { ...prev!.contact_data, status: 'NotBooked' } }))
            showToastMessage('success', res?.data?.message)
        }).catch((error) => {
            showToastMessage('error', 'Failed to unbook contact');
        })
    }

    const unbook_delete_contact = (contactId: string) => {
        CommandCenterService.unbook_delete_contact(contactId, scenario_id).then((res) => {
            showToastMessage('success', res?.data?.message)
        }).catch((error) => {
            showToastMessage('error', 'Failed to delete contact');
        })
    }

    const get_all_ground_stations = () => {
        GroundStationService.getGroundStationsByMissionId()?.then((res) => {
            if (res?.data) {
                set_all_ground_stations(res?.data)
            }
        }).catch((err) => {
            console.error(err);
        });
    }

    const structure_schedule_date = (schedule: any) => {
        let updated_data: ScheduleTask[] = schedule && schedule.map(task => {
            return {
                ...task,
                "properties": {
                    ...task?.['properties'],
                    "extraDetails": task?.properties?.['extraDetails'] ?? task?.properties?.['ExtraDetails'],
                    "freqBand": task?.properties?.['freqBand'] ?? task?.properties?.['FreqBand'],
                    "groundStationName": task?.properties?.['groundStationName'] ?? task?.properties?.['GroundStationName'],
                    "payloadName": task?.properties?.['payloadName'] ?? task?.properties?.['PayloadName'],
                    "sequenceName": task?.properties?.['sequenceName'] ?? task?.properties?.['SequenceName'],
                }
            }
        })
        return updated_data
    }


    const select_schedule = (schedule: ScheduleData, executedTasks: ScheduleTask[] = []) => {
        if (!schedule && !executedTasks.length) return
        if (schedule) {
            const Data = schedule?.['scheduledTasks']
            setScheduleDetails({
                start_time: new Date(schedule?.['startTimeISO8601'] as string).getTime() / 1000,
                name: schedule?.['scheduleName'] as string,
            })
            const schedule_data = structure_schedule_date(Data)
            setScheduleData(schedule_data)
            setSelectedSchedule(schedule?.['ScheduleId'])

            const ongoingTasks = schedule_data || []
            if (executedTasks.length > 0) {
                //Setting timeline data for both ongoing and executed schedule task events
                setTimelineScheduleData([...ongoingTasks, ...executedTasks])
            } else {
                //Setting timeline data for ongoing schedule task events
                setTimelineScheduleData(ongoingTasks)
            }
        } else {
            // Setting timeline data for executed schedule task events 
            setTimelineScheduleData(executedTasks)
        }
    }

    const get_executed_timeline_tasks = (executed: any) => {
        if (!executed?.length) return [];
        const mergedTaskList = executed.flatMap(item =>
            (item.scheduledTasks || []).filter(task => task.status !== 'Not Possible')
        );
        return structure_schedule_date(mergedTaskList);
    };

    const get_all_onboard_schedules = useCallback(() => {
        if (operateMode) {
            setLoading('schedule', true)
            CommandCenterService.getAllOnBoardSchedules(scenario_id).then((res) => {
                const Data = res?.data?.data?.[0]
                if (Data) {
                    setAllOnBoardSchedule(Data)
                    setOnboardScheduleDetails((prev => {
                        const active = Data?.['active'] || []
                        const available = Data?.['available'] || []
                        const planned = Data?.['planned'] || []
                        const executed = Data?.['executed'] || []

                        const allSchedule = [...active, ...available, ...executed, ...planned]
                        const ongoingSchedule = active?.[0] || null
                        const selectedSchedule = ongoingSchedule || allSchedule?.find(schedule => schedule?.scheduleId === prev?.selectedSchedule?.scheduleId) || allSchedule?.[0]
                        const selectedTask = selectedSchedule?.['scheduledTasks']?.find(task => task?.taskId === prev?.selectedTask?.taskId) || selectedSchedule?.['scheduledTasks']?.find(task => task?.status !== 'Not Possible')
                        const selectedTelecommand = selectedTask?.['commandsList']?.find(command => command?.commandId === prev?.selectedTelecommand?.commandId) || selectedTask?.['commandsList']?.[0]
                        const executedTasks = get_executed_timeline_tasks(executed)
                        if(!ongoingSchedule){
                            setScheduleData([])
                        }
                        if (ongoingSchedule || executedTasks.length > 0) {
                            select_schedule(ongoingSchedule, executedTasks)
                        }
                        return {
                            ...prev,
                            scheduleData: {
                                ...prev?.scheduleData,
                                executed: executed,
                                planned: planned,
                                active: active,
                                available: available,
                            },
                            selectedSchedule: selectedSchedule,
                            selectedTask: selectedTask,
                            selectedTelecommand: selectedTelecommand,
                        }
                    }))
                } else {
                    resetScheduleData()
                    setLoading('schedule', false)
                }
            }).catch(err => {
                resetScheduleData()
                showToastMessage('error', 'Failed to get schedule details');
                setLoading('schedule', false)
            })
        }
    }, [
        operateMode,
        scenario_id,
        setLoading,
        setAllOnBoardSchedule,
        setOnboardScheduleDetails,
        select_schedule,
        resetScheduleData,
        showToastMessage,
    ])

    const generate_schedule = (payload: any): Promise<string | any> => {
        return new Promise((resolve, reject) => {
            CommandCenterService.generateOperateSchedule(payload)
                .then((response) => {
                    if (response) {
                        // Assuming these are async functions, we should wait for them to complete
                        return Promise.all([
                            get_all_onboard_schedules(),
                            // get_ongoing_schedule()
                        ]).then(() => {
                            resolve('success');
                        });
                    } else {
                        resolve('No response received');
                    }
                })
                .catch((err) => {
                    console.error('Error generating schedule:', err);
                    reject(err?.response?.data || 'An error occurred');
                });
        });
    };

    const generate_adhoc_task_schedule = (payload: any): Promise<string | any> => {
        return new Promise((resolve, reject) => {
            CommandCenterService.generateAdhocTaskSchedule(payload)
                .then((response) => {
                    if (response) {
                        return Promise.all([
                            get_all_onboard_schedules(),
                        ]).then(() => {
                            resolve('success');
                        });
                    } else {
                        resolve('No response received');
                    }
                })
                .catch((err) => {
                    console.error('Error generating adhoc task schedule:', err);
                    reject(err?.response?.data || 'An error occurred');
                });
        });
    };

    const import_schedule = (payload: any, name: string): Promise<string | any> => {
        return new Promise((resolve, reject) => {
            CommandCenterService.importOperateSchedule(mission_id, scenario_id, sat_id, payload, name)
                .then((response) => {
                    if (response) {
                        // Assuming these are async functions, we should wait for them to complete
                        return Promise.all([
                            get_all_onboard_schedules(),
                            // get_ongoing_schedule()
                        ]).then(() => {
                            showToastMessage('success', response?.data?.message || 'Schedule imported successfully')
                            resolve('success');
                        });
                    } else {
                        resolve('No response received');
                    }
                })
                .catch((err) => {
                    console.error('Error generating schedule:', err);
                    const errorMessage = err?.response?.data?.message || 'An error occurred';
                    showToastMessage('error', errorMessage);
                    reject(err?.response?.data || 'An error occurred');
                });
        });
    };

    const send_operate_task_command = (task_type: string, execute_now: boolean, payload: any): Promise<string | any> => {
        return new Promise((resolve, reject) => {
            if (task_type === 'adhoc') {
                const contact_id = payload?.contactId
                const task_id = payload?.taskId
                CommandCenterService.postOperateAdhocTaskCommand(contact_id, task_id, scenario_id, truetwin_id, execute_now)
                    .then((response) => {
                        if (response) {
                            // Assuming these are async functions, we should wait for them to complete
                            return Promise.all([
                                get_all_onboard_schedules(),
                                // get_ongoing_schedule()
                            ]).then(() => {
                                resolve('success');
                            });
                        } else {
                            resolve('No response received');
                        }
                    })
                    .catch((err) => {
                        console.error('Error generating schedule:', err);
                        reject(err?.response?.data || 'An error occurred');
                    });
            } else if (task_type === 'schedule') {
                CommandCenterService.postOperateScheduleTaskCommand(scenario_id, payload)
                    .then((response) => {
                        if (response) {
                            // Assuming these are async functions, we should wait for them to complete
                            return Promise.all([
                                get_all_onboard_schedules(),
                                // get_ongoing_schedule()
                            ]).then(() => {
                                resolve('success');
                            });
                        } else {
                            resolve('No response received');
                        }
                    })
                    .catch((err) => {
                        console.error('Error generating schedule:', err);
                        reject(err?.response?.data || 'An error occurred');
                    });
            } else {
                reject('Invalid task type');
            }
        });
    };

    const send_operate_set_obc_task_command = (task_type: string, execute_now: boolean, payload: any): Promise<string | any> => {
        return new Promise((resolve, reject) => {
            if (task_type === 'set_obc_curr_time') {
                const contact_id = payload?.contactId
                CommandCenterService.postOperateSetOBCTaskCommand(contact_id, scenario_id, truetwin_id, execute_now)
                    .then((response) => {
                        if (response) {
                            // Assuming these are async functions, we should wait for them to complete
                            return Promise.all([
                                get_all_onboard_schedules(),
                                // get_ongoing_schedule()
                            ]).then(() => {
                                resolve('success');
                            });
                        } else {
                            resolve('No response received');
                        }
                    })
                    .catch((err) => {
                        console.error('Error generating schedule:', err);
                        reject(err?.response?.data || 'An error occurred');
                    });
            } else {
                reject('Invalid task type');
            }
        });
    };

    const send_operate_tc_command = async (
        contact_id: string,
        execute_now: boolean,
        payload: any
    ): Promise<string | any> => {
        try {
            await CommandCenterService.postOperateTcCommand(scenario_id, contact_id, sat_id, execute_now, payload);
            return 'success';
        } catch (err: any) {
            console.error('Failed to send TC command.:', err);
            throw err?.response?.data || 'An error occurred';
        }
    };

    const delete_onboard_schedule = (scheduleId: string): Promise<string | any> => {
        return new Promise((resolve, reject) => {
            CommandCenterService.deleteOnboardSchedules(scenario_id, scheduleId)
                .then((response) => {
                    if (response) {
                        // Assuming these are async functions, we should wait for them to complete
                        return Promise.all([
                            get_all_onboard_schedules(),
                            // get_ongoing_schedule()
                        ]).then(() => {
                            resolve('success');
                        });
                    } else {
                        resolve('No response received');
                    }
                })
                .catch((err) => {
                    console.error('Error generating schedule:', err);
                    reject(err?.response?.data || 'An error occurred');
                });
        });
    };

    const getSupportedSystemFaults = async () => {
        if (dashboard === 'simulation') {
            try {
                const { data } = await CommandCenterService.getSupported_system_faults(
                    mission_id,
                    scenario_id,
                    sat_grp_id,
                    sat_id
                );

                const faultData = data?.data;

                if (faultData && Object.keys(faultData).length > 0) {
                    // Process the data to reorder Enable before Disable
                    const reorderedData = Object.fromEntries(
                        Object.entries(faultData).map(([system, faults]) => [
                            system,
                            Object.fromEntries(
                                Object.entries(faults as Record<string, unknown>).sort(([a], [b]) => {
                                    const aIsEnable = a.startsWith('Enable');
                                    const bIsEnable = b.startsWith('Enable');
                                    if (aIsEnable && !bIsEnable) return -1;
                                    if (!aIsEnable && bIsEnable) return 1;
                                    return 0;
                                })
                            )
                        ])
                    );

                    setSupportedSystemFaults(reorderedData);
                } else {
                    throw new Error("Empty fault data received");
                }
            } catch (err: any) {
                const errorMessage = err?.response?.data?.message || "Failed to fetch supported system faults";
                showToastMessage('error', errorMessage);
            }
        }
    };


    let isInjecting = false;

    const postInject = async (faultScheduleId, payload: any): Promise<void> => {
        if (isInjecting) return;

        isInjecting = true;
        try {
            // Transform payload to required format
            const {
                system,
                faultType,
                faultDetails,
                executeLater,
                faultExecutionTime,
                duration
            } = payload;

            const reqBody: any = {
                system,
                faultType,
                faultDetails,
                executeLater
            };

            if (executeLater) {
                reqBody.executionType = "TimeBase";
                reqBody.executionRequirement = {
                    executionTime: faultExecutionTime,
                    ...(duration !== undefined && { duration })
                };
            }

            // If executeLater is false, do not include executionRequirement or executeLater
            const { data } = await CommandCenterService.post_inject(
                mission_id,
                scenario_id,
                faultScheduleId,
                reqBody
            );
            showToastMessage('success', data?.message || 'Fault injected');
            await fetchInjectedFaultList();
        } catch (error: any) {
            await fetchInjectedFaultList();
            const errorMsg =
                error?.response?.data?.message ??
                error?.message ??
                'Failed to inject fault';
            showToastMessage('error', errorMsg);
        } finally {
            isInjecting = false;
        }
    };



    const fetchInjectedFaultList = async () => {
        try {
            const { data } = await CommandCenterService.getInjectedFaultList(mission_id, scenario_id, sat_grp_id);
            setInjectedFaultList(data?.data || []);
            //   showToastMessage('success', data?.message || 'Injected faults retrieved successfully.');
        } catch (error: any) {
            const errorMsg =
                error?.response?.data?.message ||
                error?.message ||
                'Failed to fetch injected faults';
            showToastMessage('error', errorMsg);
        }
    };

    const cancelFaultInjected = async (faultScheduleId: string, faultId: string) => {
        try {
            await CommandCenterService.cancelInjectedFault(mission_id, scenario_id, faultScheduleId, faultId);
            showToastMessage('success', 'Fault Plan cancelled');
            await fetchInjectedFaultList();
        } catch (error: any) {
            const errorMsg =
                error?.response?.data?.message ||
                error?.message ||
                'Failed to cancel Fault Plan';
            showToastMessage('error', errorMsg);
        }
    };

    const stopFaultInjected = async (faultScheduleId: string, faultId: string) => {
        try {
            await CommandCenterService.stopInjectedFault(mission_id, scenario_id, faultScheduleId, faultId);
            showToastMessage('success', 'Fault execution stopped');
            await fetchInjectedFaultList();
        } catch (error: any) {
            const errorMsg =
                error?.response?.data?.message ||
                error?.message ||
                'Failed to stop fault execution';
            showToastMessage('error', errorMsg);
        }
    };

    return {
        all_ground_stations,
        set_all_ground_stations,
        selectedScheduleTask,
        setSelectedScheduleTask,
        selectedContact,
        setSelectedContact,
        contacts,
        configured_gs,
        setContacts,
        timelineContactData,
        planingContactData,
        scheduleData,
        timelineScheduleData,
        scheduleDetails,
        allOnBoardSchedule,
        selectedSchedule,
        supportedSystemFaults,
        injectedFaultList,


        setScheduleDetails,
        setScheduleData,
        setTimelineScheduleData,
        setSelectedSchedule,
        generate_schedule,
        generate_adhoc_task_schedule,
        send_operate_task_command,
        send_operate_set_obc_task_command,
        send_operate_tc_command,
        delete_onboard_schedule,
        get_time_based_gs_contact_details,
        import_schedule,
        setAllOnBoardSchedule,
        setPlaningContactData,
        setTimelineContactData,
        set_configured_gs,
        get_timeline_onboard_schedule_data,
        get_ongoing_schedule,
        get_onboard_schedule_data,
        get_configured_gs,
        get_gs_contact_details,
        get_all_ground_stations,
        structure_schedule_date,
        select_schedule,
        resetScheduleData,
        resetContactData,
        get_all_onboard_schedules,
        get_unique_gs,
        post_contact,
        book_contact,
        unbook_contact,
        unbook_delete_contact,
        getSupportedSystemFaults,
        postInject,
        fetchInjectedFaultList,
        cancelFaultInjected,
        stopFaultInjected

    };
};

export default useContacts;